import React from "react";
import { BadgeCheck, Crown, Star } from "lucide-react";

interface Plan {
  name: string;
  price: string;
  note?: string;
  features: string[];
  ctaLabel: string;
  ctaHref: string;
  highlighted?: boolean;
}

export default function Pricing() {
  const plans: Plan[] = [
    {
      name: "Basic",
      price: "Free",
      note: "For starters • limited monthly transactions",
      features: [
        "QR code payments (scan & pay)",
        "Euro → crypto conversion",
        "Transaction history",
        "Open‑source app"
      ],
      ctaLabel: "Use Payme.sol Basic",
      ctaHref: "https://basic.paymesol.app",
    },
    {
      name: "Pro",
      price: "0.5% / tx",
      note: "For businesses • advanced features",
      features: [
        "Real‑time in‑app notifications",
        "Crypto receipts",
        "Unlimited transactions",
        "EUR / USD / AED support"
      ],
      ctaLabel: "Go Pro with Payme.sol",
      ctaHref: "https://pro.paymesol.app",
      highlighted: true,
    },
    {
      name: "DeBros NFT",
      price: "0% / tx",
      note: "Everything included in Pro +",
      features: [
        "Not a separate plan — unlocks unlimited Pro",
        "Connect the wallet that holds your DeBros NFT to activate",
        "Pro fee is 0% / tx while the NFT remains in your connected wallet"
      ],
      ctaLabel: "",
      ctaHref: "",
    },
  ];

  return (
    <section id="pricing" className="relative overflow-hidden" data-reveal>
      <div className="mx-auto max-w-6xl px-4 py-16 relative">
        <h2 className="mb-8 text-center text-3xl font-semibold">Pricing</h2>
        <p className="mx-auto mb-10 max-w-2xl text-center text-neutral-300">
          Choose the plan that suits your needs. Start free and upgrade anytime.
        </p>
        <div className="grid grid-cols-1 gap-6 items-stretch md:grid-cols-3 md:gap-4 lg:gap-6">
          {plans.map((p, idx) => (
            <div
              key={p.name}
              data-reveal
              style={{ ['--reveal-delay' as any]: `${idx * 110}ms` }}
              className={`relative flex flex-col rounded-xl border p-6 md:p-4 lg:p-6 transition-all duration-300 hover:-translate-y-0.5
                ${p.name === "DeBros NFT"
                  ? "border-amber-500/70 bg-amber-500/5 shadow-[0_0_40px_rgba(245,158,11,0.25)] hover:border-amber-400/80 hover:bg-amber-500/10 hover:shadow-[0_0_60px_rgba(245,158,11,0.35)]"
                  : p.highlighted
                  ? "border-indigo-500/60 bg-indigo-500/5 hover:border-indigo-500/50 hover:bg-indigo-500/5 hover:shadow-[0_0_40px_rgba(99,102,241,0.15)]"
                  : "border-neutral-700 bg-neutral-900/40 hover:border-neutral-600 hover:bg-neutral-900/60 hover:shadow-[0_0_40px_rgba(0,0,0,0.25)]"}
              `}
            >
              {p.highlighted && (
                <span className="absolute -top-3 left-6 rounded-full border border-indigo-500/40 bg-indigo-600 px-3 py-1 text-xs md:px-2 md:py-0.5 font-semibold text-white">
                  Most used
                </span>
              )}
              {p.name === "DeBros NFT" && (
                <span className="absolute -top-3 left-6 rounded-full border border-amber-400/40 bg-amber-500 px-3 py-1 text-xs md:px-2 md:py-0.5 font-semibold text-black">
                  Holders Only
                </span>
              )}
              <div className="flex items-baseline justify-between gap-4 md:gap-3">
                <h3 className="flex items-center gap-2 md:gap-1.5 text-xl md:text-lg lg:text-xl font-semibold text-white">
                  {p.name === "Pro" ? (
                    <Star className="size-5 md:size-4 lg:size-5 text-indigo-400" />
                  ) : p.name === "DeBros NFT" ? (
                    <Crown className="size-5 md:size-4 lg:size-5 text-amber-400" />
                  ) : (
                    <BadgeCheck className="size-5 md:size-4 lg:size-5 text-indigo-400" />
                  )}
                  {p.name}
                </h3>
                <div className="text-right">
                  <div className="text-2xl md:text-xl lg:text-2xl font-bold text-white">{p.price}</div>
                  {p.note && <div className="text-xs text-neutral-400">{p.note}</div>}
                </div>
              </div>
              <ul className="mt-6 space-y-2 text-sm md:text-xs lg:text-sm text-neutral-300">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <span className={`mt-1 inline-block h-1.5 w-1.5 rounded-full ${p.name === "DeBros NFT" ? "bg-amber-500" : "bg-indigo-500"}`} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              {p.ctaHref && (<div className="mt-auto pt-4">
                <a
                  href={p.ctaHref}
                  className={`inline-flex w-full items-center justify-center rounded-lg px-4 py-2 text-sm md:px-3 md:py-2 md:text-xs lg:px-4 lg:py-2 lg:text-sm font-semibold ${
                    p.name === "DeBros NFT"
                      ? "bg-amber-500 text-black hover:bg-amber-400"
                      : p.highlighted
                      ? "bg-indigo-600 text-white hover:bg-indigo-500"
                      : "bg-neutral-800 text-white hover:bg-neutral-700"
                  }`}
                  target="_blank" rel="noreferrer noopener"
                >
                  {p.ctaLabel}
                </a>
              </div>)}
              {false && p.name === "Pro"}
            </div>
          ))}
        </div>

        <div className="mx-auto mt-6 max-w-3xl text-center text-sm text-neutral-400">
          <p>
            <strong>Basic:</strong> Free with limited transactions — core POS features. 
            <strong> Pro:</strong> 0.5% per transaction — unlimited transactions, notifications, receipts, EUR/USD/AED.

          </p>
          <p className="mt-1">
            <strong>DeBros NFT:</strong> A holder benefit that sets the Pro fee to 0% / tx and unlocks unlimited Pro while the NFT remains in your connected wallet.
          </p>
        </div>

        <div className="mt-12" aria-hidden />
      </div>
    </section>
  );
}
